import React, { useState, useEffect } from 'react';

const PerformanceGrid = ({ vesselName, voyageNumber }) => {
  const [performanceData, setPerformanceData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPerformanceData = async () => {
      if (!vesselName) return;

      setIsLoading(true);
      try {
        const params = new URLSearchParams();
        params.append('vesselName', vesselName.trim());
        if (voyageNumber) {
          params.append('voyageNumber', voyageNumber.trim());
        }

        console.log('🔍 PerformanceGrid fetching:', `/api/performance/status?${params}`);

        const response = await fetch(`/api/performance/status?${params}`);
        const data = await response.json();

        console.log('🔍 PerformanceGrid received:', data);

        if (data.performances && data.performances.length > 0) {
          console.log(`🔍 PerformanceGrid: ${data.performances.length} performance records received`);

          // Group by port, keeping the most recent record with actual delay data
          const groupedData = data.performances.reduce((acc, perf) => {
            const key = `${perf.port_code}-${perf.port_type}`;

            if (!acc[key]) {
              // First record for this port
              acc[key] = perf;
            } else {
              // Keep the record with actual delay data (prefer records with departure delays for POL)
              const currentHasDepartureDelay = perf.departure_delay_minutes !== null;
              const existingHasDepartureDelay = acc[key].departure_delay_minutes !== null;

              // For POL ports, prefer records with departure delays
              if (perf.port_type === 'POL' && currentHasDepartureDelay && !existingHasDepartureDelay) {
                acc[key] = perf;
              }
              // For any port, prefer records with arrival delays
              else if (perf.arrival_delay_minutes !== null && acc[key].arrival_delay_minutes === null) {
                acc[key] = perf;
              }
            }
            return acc;
          }, {});

          const finalPerformances = Object.values(groupedData);
          console.log('🔍 PerformanceGrid grouped data:', finalPerformances.length, 'ports');

          finalPerformances.forEach(perf => {
            console.log(`🔍 Port ${perf.port_code} (${perf.port_type}):`, {
              arrival: perf.arrival_delay_minutes,
              departure: perf.departure_delay_minutes
            });
          });

          setPerformanceData({
            performances: finalPerformances,
            summary: data.summary || calculateSummary(data.performances)
          });
        } else {
          console.log('🔍 PerformanceGrid: No performance data found');
          setPerformanceData({ performances: [], summary: null });
        }
      } catch (err) {
        console.error('Error fetching performance data:', err);
        setError('Failed to fetch performance data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPerformanceData();
  }, [vesselName, voyageNumber]);

  const calculateSummary = (performances) => {
    const totalRecords = performances.length;
    const onTimeCount = performances.filter(p => {
      const delay = p.arrival_delay_minutes;
      if (!delay) return false;
      const delayDays = delay / (24 * 60);
      return delayDays >= -2 && delayDays <= 2;
    }).length;
    const earlyCount = performances.filter(p => {
      const delay = p.arrival_delay_minutes;
      if (!delay) return false;
      return delay < -2 * 24 * 60;
    }).length;

    const avgDelay = performances.length > 0
      ? performances.reduce((sum, p) => sum + (p.arrival_delay_minutes || 0), 0) / performances.length
      : 0;

    return {
      totalRecords,
      onTimeRate: Math.round((onTimeCount / totalRecords) * 100),
      avgDelay: Math.round(avgDelay),
      earlyArrivals: earlyCount
    };
  };

  const getDelayCellStyle = (delayMinutes) => {
    if (!delayMinutes) return 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500';

    const delayDays = delayMinutes / (24 * 60);

    // Early arrivals
    if (delayMinutes < -2 * 24 * 60) return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium';
    if (delayMinutes < 0) return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 font-medium';

    // Late arrivals/departures
    if (delayDays <= 2) return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 font-medium';
    if (delayDays <= 7) return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 font-medium';
    if (delayDays <= 14) return 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 font-medium';
    return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 font-medium';
  };

  const formatDelay = (minutes) => {
    if (!minutes) return '—';
    const days = Math.floor(Math.abs(minutes) / (24 * 60));
    const hours = Math.floor((Math.abs(minutes) % (24 * 60)) / 60);
    const mins = Math.abs(minutes) % 60;
    const sign = minutes > 0 ? '+' : '-';
    return `${sign}${days}d ${hours}h ${mins}m`;
  };

  if (isLoading) return <div className="text-center py-8 text-gray-600">Loading performance data...</div>;
  if (error) return <div className="text-center py-8 text-red-600">{error}</div>;
  if (!performanceData || performanceData.performances.length === 0) {
    return <div className="text-center py-8 text-gray-600">No performance data found for {vesselName}</div>;
  }

  // Separate POL and POD ports
  const polPorts = performanceData.performances.filter(p => p.port_type === 'POL');
  const podPorts = performanceData.performances.filter(p => p.port_type === 'POD');

  return (
    <div className="space-y-6">
      {/* Legend */}
      <div className="flex justify-center space-x-6 text-sm">
        <span className="flex items-center"><span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span> On Time (±2 days)</span>
        <span className="flex items-center"><span className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></span> Minor (2-7 days)</span>
        <span className="flex items-center"><span className="w-3 h-3 bg-orange-500 rounded-full mr-2"></span> Moderate (7-14 days)</span>
        <span className="flex items-center"><span className="w-3 h-3 bg-red-500 rounded-full mr-2"></span> Major (>14 days)</span>
        <span className="flex items-center"><span className="w-3 h-3 bg-blue-500 rounded-full mr-2"></span> Early (>2 days)</span>
      </div>

      {/* Performance Grid */}
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm border-collapse table-fixed">
            <colgroup>
              <col className="w-[150px]" />
              <col className="w-[80px]" />
              <col className="w-[80px]" />
            </colgroup>

            <thead>
              <tr>
                <th className="px-3 py-3 text-left border-b border-r dark:border-gray-600 bg-gray-50 dark:bg-gray-800 font-medium sticky left-0 text-gray-900 dark:text-gray-100">
                  Performance
                </th>
                <th className="px-3 py-3 text-center border-b border-r dark:border-gray-600 bg-gray-50 dark:bg-gray-800">
                  <div className="font-medium text-gray-900 dark:text-gray-100">Arrival</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">ETA vs ATA</div>
                </th>
                <th className="px-3 py-3 text-center border-b dark:border-gray-600 bg-gray-50 dark:bg-gray-800">
                  <div className="font-medium text-gray-900 dark:text-gray-100">Departure</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">ETD vs ATD</div>
                </th>
              </tr>
            </thead>

            <tbody className="text-xs">
              {/* POL Section */}
              {polPorts.length > 0 && (
                <>
                  <tr>
                    <td colSpan="3" className="px-3 py-2 text-blue-600 dark:text-blue-400 font-medium bg-blue-50 dark:bg-blue-900/20 border-b dark:border-gray-600">
                      🔵 Load Ports (POL)
                    </td>
                  </tr>
                  {polPorts.map((port, idx) => (
                    <tr key={`pol-${idx}`} className="border-b dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-3 py-2 border-r dark:border-gray-600 font-medium sticky left-0 bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
                        {port.port_code}
                      </td>
                      <td className={`delay-cell text-center px-2 py-2 border-r dark:border-gray-600 ${getDelayCellStyle(port.arrival_delay_minutes)}`}>
                        {formatDelay(port.arrival_delay_minutes)}
                      </td>
                      <td className={`delay-cell text-center px-2 py-2 dark:border-gray-600 ${getDelayCellStyle(port.departure_delay_minutes)}`}>
                        {formatDelay(port.departure_delay_minutes)}
                      </td>
                    </tr>
                  ))}
                </>
              )}

              {/* POD Section */}
              {podPorts.length > 0 && (
                <>
                  <tr>
                    <td colSpan="3" className="px-3 py-2 text-green-600 dark:text-green-400 font-medium bg-green-50 dark:bg-green-900/20 border-b dark:border-gray-600">
                      🟢 Discharge Ports (POD)
                    </td>
                  </tr>
                  {podPorts.map((port, idx) => (
                    <tr key={`pod-${idx}`} className="border-b dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-3 py-2 border-r dark:border-gray-600 font-medium sticky left-0 bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
                        {port.port_code}
                      </td>
                      <td className={`delay-cell text-center px-2 py-2 border-r dark:border-gray-600 ${getDelayCellStyle(port.arrival_delay_minutes)}`}>
                        {formatDelay(port.arrival_delay_minutes)}
                      </td>
                      <td className="text-center px-2 py-2 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-400">
                        —
                      </td>
                    </tr>
                  ))}
                </>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 border-t dark:border-gray-600 text-xs text-gray-600 dark:text-gray-300">
          <div className="flex justify-between items-center">
            <div className="text-gray-900 dark:text-gray-100">
              <strong>Key:</strong> Arrival delays compare ETA vs ATA | Departure delays compare ETD vs ATD
            </div>
            <div className="text-gray-900 dark:text-gray-100">
              <span>POD ports show arrival delays only</span>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      {performanceData.summary && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 text-center">
            <div className="text-sm text-gray-600 dark:text-gray-300">Avg Arrival Delay</div>
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">
              {formatDelay(performanceData.summary.avgDelay)}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 text-center">
            <div className="text-sm text-gray-600 dark:text-gray-300">On-Time Rate</div>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {performanceData.summary.onTimeRate}%
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 text-center">
            <div className="text-sm text-gray-600 dark:text-gray-300">Total Records</div>
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {performanceData.summary.totalRecords}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 text-center">
            <div className="text-sm text-gray-600 dark:text-gray-300">Early Arrivals</div>
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {performanceData.summary.earlyArrivals}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformanceGrid;